/**
 * @documental
 * @export MenuButton
 */
import { FC, FocusEvent, ReactNode, useCallback, useEffect, useRef, useState } from "react";

import { Bounds } from "capitalrx-components-2.0/lib//utilities/BoundsObserver/Bounds";
import { getHtmlPropsFromBaseProps } from "capitalrx-components-2.0/lib//utilities/getHtmlPropsFromBaseProps";
import { isDescendentOf } from "capitalrx-components-2.0/lib//utilities/isDescendentOf";
import { withSubComponents } from "capitalrx-components-2.0/lib//utilities/withSubComponents";
import { Button, ButtonProps } from "capitalrx-components-2.0/lib/general/Button";
import { Icon } from "capitalrx-components-2.0/lib/general/Icon";
import { Menu, MenuSubComponents } from "capitalrx-components-2.0/lib/general/Menu";
import { AdjustmentBounds, ElementBounds } from "capitalrx-components-2.0/lib/general/Overlay";
import { useId } from "capitalrx-components-2.0/lib/hooks/useId/useId";
import { Shelf } from "capitalrx-components-2.0/lib/layout";
import classNames, { Argument } from "classnames";

import ButtonStyles from "../Button/styles.module.scss";
import styles from "./styles.module.scss";

/**
 * The `MenuButton` component properties.
 */
export interface MenuButtonProps extends Omit<ButtonProps, "onClick" | "aria-expanded"> {
  /**
   * The menu CSS class name.
   */
  readonly menuClassName?: Argument;

  /**
   * The button label.
   */
  readonly label?: ReactNode;

  /**
   * Hides or shows the carret icon.
   */
  readonly caretIcon?: boolean;

  /**
   * Determines whether the caret changes orientation when the menu expands.
   */
  readonly dynamicCaret?: boolean;

  /** Called when the menu is opened. */
  readonly onMenuOpen?: () => void;

  /** Called when the menu is closed. */
  readonly onMenuClose?: () => void;
}

/**
 * A button that when clicked presents a menu of items for selection.
 */
export const MenuButton = withSubComponents<FC<MenuButtonProps>, MenuSubComponents>(
  {
    Item: Menu.Item,
    ComponentItem: Menu.ComponentItem,
    Group: Menu.Group,
    LinkItem: Menu.LinkItem,
  },
  function MenuButton(props) {
    const {
      menuClassName,
      label,
      children,
      caretIcon = true,
      onMenuOpen,
      onMenuClose,
      dynamicCaret = false,
      testId,
      "aria-controls": ariaControlsProp,
      "aria-haspopup": ariaHaspopupProp,
    } = props;
    const [menuVisible, setMenuVisible] = useState(false);
    const buttonRef = useRef<HTMLButtonElement | null>(null);
    // id for the menu; if caller supplies aria-controls we use that value, otherwise generate one
    const generatedMenuId = useId();
    const menuId = ariaControlsProp ?? generatedMenuId;

    const htmlBaseProps = getHtmlPropsFromBaseProps(props, {
      className: [
        styles.MenuButtonContainer,
        "menu-button",
        {
          opened: menuVisible,
          focused: menuVisible,
        },
      ],
      testIdPrefix: "menu-button",
    });
    const menuContainerClassName = classNames({
      "items--menu-is-open": menuVisible,
    });

    const clickHandler = useCallback(() => {
      setMenuVisible((current) => !current);
    }, []);

    const menuCloseHandler = useCallback(() => {
      setMenuVisible(false);
      buttonRef.current?.focus();
    }, []);

    const blurHandler = useCallback((ev: FocusEvent<HTMLSpanElement>) => {
      const { currentTarget, relatedTarget } = ev;

      if (relatedTarget && (relatedTarget === currentTarget || isDescendentOf(currentTarget, relatedTarget))) return;

      setMenuVisible(false);
    }, []);

    useEffect(() => {
      if (menuVisible) onMenuOpen?.();
      else onMenuClose?.();
    }, [menuVisible, onMenuClose, onMenuOpen]);

    const menuBoundsChangeHandler = useCallback((elementBounds: ElementBounds, containerBounds: Bounds): AdjustmentBounds => {
      const { bounds } = elementBounds;
      let { top, left, width } = bounds;
      const { height } = bounds;

      const { top: containerTop, left: containerLeft, width: containerWidth, height: containerHeight } = containerBounds;
      const containerRight = containerLeft + containerWidth;
      const containerBottom = containerTop + containerHeight;

      if (!buttonRef.current) return bounds;
      const buttonRect = buttonRef.current.getBoundingClientRect();

      const leftDistance = Math.floor(buttonRect.left) - containerLeft;
      const rightDistance = containerRight - buttonRect.right;

      // Try right-aligned positioning if there's more room on the left
      if (rightDistance <= leftDistance) {
        left -= width - buttonRect.width;
      }

      // Calculate available space above and below the button
      const spaceBelow = containerBottom - buttonRect.bottom;
      const spaceAbove = buttonRect.top - containerTop;
      const totalAvailableHeight = containerHeight;

      // Decide whether to show below or above based on available space
      let constrainedHeight: number | undefined;

      const menuFitsAbove = height <= spaceAbove;

      if (menuFitsAbove) {
         // Menu fits above - flip it
         top = buttonRect.top - height;
      } else if (height >= totalAvailableHeight) {
        // Menu is taller than container - fill the container height
        top = containerTop;
        constrainedHeight = totalAvailableHeight;
      } else {
        // Menu doesn't fit either way - use the larger space and constrain
        if (spaceBelow >= spaceAbove) {
          // Stay below, constrain to available space
          constrainedHeight = spaceBelow;
        } else {
          // Flip above and constrain to available space
          top = containerTop;
          constrainedHeight = spaceAbove;
        }
      }

      // Constrain width to container width
      if (width > containerWidth) {
        width = containerWidth;
      }

      // If menu extends past right edge, either shrink or reposition
      const rightEdge = left + width;
      if (rightEdge > containerRight) {
        const overflow = rightEdge - containerRight;
        left -= overflow;
      }

      // If menu extends past left edge, clamp to left and shrink width if needed
      if (left < containerLeft) {
        left = containerLeft;
        if (left + width > containerRight) {
          width = containerRight - left;
        }
      }

      // Final safety check: ensure width doesn't exceed available space
      width = Math.max(0, Math.min(width, containerRight - left));

      return {
        ...bounds,
        left,
        top,
        width,
        ...(constrainedHeight !== undefined && { height: constrainedHeight }),
      };
    }, []);

    return (
      <span {...htmlBaseProps} tabIndex={-1} onBlur={blurHandler}>
        <Button
          {...props}
          aria-haspopup={ariaHaspopupProp ?? "menu"}
          aria-expanded={menuVisible}
          aria-controls={menuId}
          ref={buttonRef}
          className={classNames(styles.MenuButton, { down: menuVisible })}
          testId={testId}
          onClick={clickHandler}
        >
          {label && !caretIcon && <>{label}</>}
          {!label && caretIcon && (
            <Icon
              className={classNames(ButtonStyles.Icon, "dropdown-arrow")}
              name={dynamicCaret && menuVisible ? "chevron-up" : "chevron-down"}
            />
          )}
          {label && caretIcon && (
            <Shelf spacing="small">
              {label}
              {caretIcon && (
                <Icon
                  className={classNames(ButtonStyles.Icon, "dropdown-arrow")}
                  name={dynamicCaret && menuVisible ? "chevron-up" : "chevron-down"}
                />
              )}
            </Shelf>
          )}
        </Button>
        <div className={menuContainerClassName}>
          <Menu
            id={menuId}
            containerClassName={styles.MenuContainer}
            className={[styles.Menu, menuClassName]}
            visible={menuVisible}
            onClose={menuCloseHandler}
            onBoundsChange={menuBoundsChangeHandler}
          >
            {children}
          </Menu>
        </div>
      </span>
    );
  },
);
